/**
 * Tools — the tool registry: the catalog of node "tools" agents are built from.
 *
 * Mirrors `GET /api/tools`. Each card shows the tool's category, what it does,
 * its config parameters, and its inputs/outputs. The Permission-Aware Retrieval
 * tool is flagged as filter + audit enforced (no privileged bypass), which is
 * why agents built from it stay permission-aware.
 */

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchTools, type ToolDef } from '../lib/tools';

function ToolCard({ tool }: { tool: ToolDef }): ReactNode {
  return (
    <li className="card flex h-full flex-col gap-2">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h2 className="text-base font-semibold text-navy-900">{tool.name}</h2>
          <span className="text-[11px] uppercase tracking-wide text-steel-400">
            {tool.category}
          </span>
        </div>
        {tool.permission_enforced && (
          <span className="shrink-0 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-medium text-emerald-800">
            Filter + audit
          </span>
        )}
      </div>
      <p className="text-sm text-steel-600">{tool.description}</p>

      {tool.config_params.length > 0 && (
        <div className="mt-1">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-steel-400">
            Config
          </p>
          <ul className="mt-1 space-y-0.5">
            {tool.config_params.map((p) => (
              <li key={p.name} className="text-xs text-steel-600">
                <span className="font-mono text-navy-700">{p.name}</span>
                <span className="text-steel-400"> · {p.type}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-auto flex flex-wrap gap-x-4 gap-y-1 pt-2 text-[11px] text-steel-500">
        {tool.inputs.length > 0 && <span>in: {tool.inputs.join(', ')}</span>}
        {tool.outputs.length > 0 && <span>out: {tool.outputs.join(', ')}</span>}
      </div>
    </li>
  );
}

export function Tools(): ReactNode {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['tools'],
    queryFn: fetchTools,
  });

  return (
    <div className="animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-navy-900">Tool Registry</h1>
          <p className="mt-1 text-sm text-steel-600">
            The approved building blocks agents are assembled from. Open the{' '}
            <Link to="/agents" className="text-navy-700 underline">
              Agent Builder
            </Link>{' '}
            to compose them into a workflow.
          </p>
        </div>
      </div>

      {isLoading && <p className="mt-6 text-sm text-steel-500">Loading tools…</p>}
      {isError && (
        <p className="mt-6 text-sm text-rose-600">
          {error instanceof Error ? error.message : 'Failed to load tools'}
        </p>
      )}

      {data && (
        <ul className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {data.map((tool) => (
            <ToolCard key={tool.id} tool={tool} />
          ))}
        </ul>
      )}
    </div>
  );
}

export default Tools;
